<script setup>
import { reactive, watch } from "vue";

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  suppliers: {
    type: Array,
    default: () => [],
  },
  categories: {
    type: Array,
    default: () => [],
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  showFinanceFields: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue"]);

const form = reactive({ ...props.modelValue });
let syncingFromParent = false;

watch(
  () => props.modelValue,
  (value) => {
    if (!value) {
      return;
    }
    syncingFromParent = true;
    Object.assign(form, value);
    syncingFromParent = false;
  },
  { deep: true, immediate: true },
);

watch(
  form,
  (value) => {
    if (syncingFromParent) {
      return;
    }
    emit("update:modelValue", { ...value });
  },
  { deep: true },
);
</script>

<template>
  <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Item</label>
      <input
        v-model="form.item"
        :disabled="readonly"
        class="input px-4 py-2 border-2 border-indigo-200 dark:border-indigo-600 dark:bg-slate-900 dark:text-white"
        placeholder="e.g. Replacement oxygen cylinders"
      />
    </div>

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Supplier</label>
      <select
        v-model.number="form.supplier_id"
        :disabled="readonly"
        class="input px-4 py-2 dark:bg-slate-900 dark:text-white dark:border-slate-600">
        <option value="">Select supplier</option>
        <option v-for="supplier in suppliers" :key="supplier.id" :value="supplier.id">
          {{ supplier.company }}
        </option>
      </select>
    </div>

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Category</label>
      <select
        v-model.number="form.category_id"
        :disabled="readonly"
        class="input px-4 py-2 dark:bg-slate-900 dark:text-white dark:border-slate-600">
        <option value="">Select category</option>
        <option v-for="category in categories" :key="category.id" :value="category.id">
          {{ category.name }}
        </option>
      </select>
    </div>

    <div v-if="showFinanceFields" class="flex flex-col gap-2">
      <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Cost (ex VAT)</label>
      <input
        type="number"
        min="0"
        step="0.01"
        v-model.number="form.cost_ex_vat"
        :disabled="readonly"
        class="input px-4 py-2 dark:bg-slate-900 dark:text-white dark:border-slate-600"
        placeholder="0.00"
      />
    </div>

    <div class="md:col-span-2 flex flex-col gap-2">
      <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Description</label>
      <textarea
        v-model="form.description"
        :disabled="readonly"
        class="input px-4 py-2 dark:bg-slate-900 dark:text-white dark:border-slate-600" rows="3"
        placeholder="Why the requisition is needed"
      ></textarea>
    </div>

    <div class="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Purpose</label>
        <textarea
          v-model="form.purpose"
          :disabled="readonly"
          class="input px-4 py-2 dark:bg-slate-900 dark:text-white dark:border-slate-600" rows="2"></textarea>
      </div>
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Additional Information</label>
        <textarea
          v-model="form.information"
          :disabled="readonly"
          class="input px-4 py-2 dark:bg-slate-900 dark:text-white dark:border-slate-600" rows="2"></textarea>
      </div>
    </div>

    <div v-if="showFinanceFields" class="grid grid-cols-1 md:grid-cols-3 gap-4 md:col-span-2">
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Approved To Order At</label>
        <input
          type="datetime-local"
          v-model="form.approved_to_order_at"
          :disabled="readonly"
          class="input px-4 py-2 dark:bg-slate-900 dark:text-white dark:border-slate-600" />
      </div>
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Payment Method</label>
        <input
          v-model="form.payment_method"
          :disabled="readonly"
          class="input px-4 py-2 dark:bg-slate-900 dark:text-white dark:border-slate-600" placeholder="Bank Transfer" />
      </div>
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-slate-600 dark:text-slate-300">Payment Issued At</label>
        <input
          type="datetime-local"
          v-model="form.payment_issued_at"
          :disabled="readonly"
          class="input px-4 py-2 dark:bg-slate-900 dark:text-white dark:border-slate-600" />
      </div>
    </div>

  </div>
</template>
